import React from 'react';
import TagsCarousel from '../TagsCarousel';
import { childTagSwiperID, parentTagSwiperID } from '@/constant/configs';
import { useSearchParams } from 'next/navigation';
import {
  RestaurantTag,
  RestaurantTagsResponse,
} from '@/lib/types/customer/RestaurantTags';
import useAdBanners from '@/hooks/useAdBanners';
import AdBannerImage from '../shared/AdBannerImage';

function flattenTags(tags: RestaurantTag[]): RestaurantTag[] {
  const result: RestaurantTag[] = [];

  function flatten(tagList: RestaurantTag[]) {
    tagList.forEach((tag) => {
      // Add the current tag to the result
      result.push({ ...tag, children: [] }); // Exclude children in the flat structure
      // Recursively flatten the children
      if (tag.children && tag.children.length > 0) {
        flatten(tag.children);
      }
    });
  }

  flatten(tags);
  return result;
}

export default function TagsContainer({
  tagsData,
}: {
  tagsData: RestaurantTagsResponse;
}) {
  const [scrollToSlideFirstTime, setScrollToSlideFirstTime] =
    React.useState(true);
  const swiperRefs = React.useRef<Map<string, any>>(new Map());
  const searchParams = useSearchParams();
  const tagIdsInSearchParams = searchParams.get('tag_ids')?.split(',') || [];
  const tagList = tagsData?.data || [];
  let parentTagInSearchParams: RestaurantTag | undefined;
  let childTagInSearchParams: RestaurantTag | undefined;
  let subTags: RestaurantTag[] = [];

  if (tagList && tagList?.length > 0 && tagIdsInSearchParams.length > 0) {
    // Flatten the tags to find parent and child tags
    const flattenedTags = flattenTags(tagList);

    parentTagInSearchParams = tagList.find(
      (tag: RestaurantTag) =>
        tagIdsInSearchParams.includes(tag.id.toString()) && !tag.parent_id
    );

    childTagInSearchParams = flattenedTags.find(
      (tag: RestaurantTag) =>
        tagIdsInSearchParams.includes(tag.id.toString()) && tag.parent_id
    );

    if (parentTagInSearchParams?.id && !childTagInSearchParams?.id) {
      subTags =
        tagList.find(
          (tag: RestaurantTag) => tag.id === parentTagInSearchParams?.id
        )?.children || [];
    }

    if (childTagInSearchParams?.id && !parentTagInSearchParams?.id) {
      subTags =
        tagList.find(
          (tag: RestaurantTag) => tag.id === childTagInSearchParams?.parent_id
        )?.children || [];
    }
  }

  const setSwiperRefs = React.useCallback(
    (swiper: any, id: string) => {
      if (swiper && id) {
        const allSwiperRefs = swiperRefs.current;
        allSwiperRefs.set(id, swiper);
        swiperRefs.current = allSwiperRefs;
      }
    },
    [swiperRefs]
  );

  // Fetch ad banners when a parent tag is selected (but not a child tag)
  const shouldFetchAdBanner =
    !!parentTagInSearchParams?.id && !childTagInSearchParams?.id;
  const { data: adBannerData } = useAdBanners({
    location: 'tag_list',
    tagIds: parentTagInSearchParams?.id ? [parentTagInSearchParams.id] : [],
    enabled: shouldFetchAdBanner,
  });

  // Get the first ad banner from the response
  const adBanner = adBannerData?.data?.[0] || null;

  return (
    <>
      <div className="lg:mb-15 mb-10">
        {tagsData && tagsData.data.length > 0 && (
          <TagsCarousel
            tagsResData={tagsData}
            tagsData={tagsData.data}
            carouselId={parentTagSwiperID}
            parentTagInSearchParams={parentTagInSearchParams}
            childTagInSearchParams={childTagInSearchParams}
            swiperRefs={swiperRefs.current}
            setSwiperRefs={setSwiperRefs}
            scrollToSlideFirstTime={scrollToSlideFirstTime}
            setScrollToSlideFirstTime={setScrollToSlideFirstTime}
          />
        )}
        {subTags.length > 0 && (
          <>
            <TagsCarousel
              tagsResData={tagsData}
              tagsData={subTags}
              isChildTagCarousel={true}
              carouselId={childTagSwiperID}
              parentTagInSearchParams={parentTagInSearchParams}
              childTagInSearchParams={childTagInSearchParams}
              swiperRefs={swiperRefs.current}
              setSwiperRefs={setSwiperRefs}
              scrollToSlideFirstTime={scrollToSlideFirstTime}
              setScrollToSlideFirstTime={setScrollToSlideFirstTime}
            />
          </>
        )}
        {/* Display ad banner below child tags if available */}
        {adBanner && (
          <AdBannerImage
            mobileImage={adBanner.mobile_image}
            desktopImage={adBanner.desktop_image}
            alt={adBanner.shop || 'Ad Banner'}
            className={'tags-ad-banner'}
            link={adBanner.link}
            adBanner={adBanner}
            location="tags_list"
            tagName={parentTagInSearchParams?.name}
          />
        )}
      </div>
    </>
  );
}
